<template>
  <ContractReviewDisplayComponent
    :accountData="accountData"
    :contractsToShow="contracts"
    :signerFields="signerFields"
    :companyFields="companyFields"
    :generatorFields="[]"
    :hasGenerator="false"
    @createContainerForStartSigningReady="
      $emit('createContainerForStartSigningReady', $event)
    "
    @disableChangingInformation="$emit('disableChangingInformation')"
  />
</template>

<script setup lang="ts">
import { useCompanyFinder } from "@/composables/useCompanyFinder";
import { Account } from "@/types";
import { reactive, computed } from "vue";
import ContractReviewDisplayComponent from "@/signup/ContractReviewDisplayComponent.vue";
import { useI18n } from "vue-i18n";
import { GENERIC_CONTRACTS } from "@/signup/default/contracts";

interface Contract {
  id: number;
  key: string;
  contract_title: string;
  contract_description: string;
  include: boolean;
  can_include: boolean;
}

const props = defineProps<{
  accountData: Account;
}>();

defineEmits<{
  createContainerForStartSigningReady: [
    createContainerForStartSigning: () => Promise<void>,
  ];
  disableChangingInformation: [];
}>();

const { t } = useI18n();
const { companyIndex } = useCompanyFinder(props.accountData);

const selectedCompany = computed(
  () => props.accountData.companies[companyIndex.value]
);

/**
 * Signer information fields displayed in the contract review
 * Shows essential account holder information
 */
const signerFields = reactive([
  { label: t("account.name"), value: props.accountData.name },
  { label: t("account.email"), value: props.accountData.email },
  {
    label: t("account.phone_number"),
    value: props.accountData.phone_number,
  },
]);

/**
 * Company information fields displayed in the contract review
 * Shows essential company registration information
 */
const companyFields = computed(() => {
  if (!selectedCompany.value) return [];

  return [
    { label: t("company.name"), value: selectedCompany.value.name },
    {
      label: t("company.registry_code"),
      value: selectedCompany.value.registry_code,
    },
    { label: t("company.address"), value: selectedCompany.value.address },
    { label: t("company.vat"), value: selectedCompany.value.vat },
  ];
});

/**
 * Generic contracts configuration for international use
 * Uses the fallback system's generic contracts (authorization and membership)
 */
const contracts = computed(() => {
  const contractsContainer: Contract[] = GENERIC_CONTRACTS.map(
    (genericContract, index) => ({
      id: index + 1,
      key: genericContract.contract_key,
      contract_title: t(genericContract.meta.title),
      contract_description: t(genericContract.meta.description),
      include: genericContract.include,
      can_include: genericContract.can_include,
    })
  );

  // Return as single container (simple 2-step flow)
  return [contractsContainer];
});
</script>

<style scoped lang="scss"></style>
